import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import axios from "axios";
import EmailChangeModal from "../../components/EmailChangeModal";

const StaffSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [userData, setUserData] = useState(null);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [settings, setSettings] = useState({
    // Notification Preferences
    emailNotifications: {
      newBooking: true,
      cancellation: true,
      reminder: true,
      review: true,
    },
    smsNotifications: {
      newBooking: false,
      cancellation: true,
      reminder: true,
    },
    // Display Preferences
    // Never pre-select a zone: R2/R3 inherit the shop location's timezone and must
    // actively choose nothing here. Starts empty until a stored value is loaded.
    timezone: "",
    language: "en",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "12h",
    // Privacy Settings
    profileVisible: true,
    showEmail: false,
    showPhone: true,
    // Booking Settings
    autoConfirm: false,
    bufferTime: 0,
    advanceBookingDays: 60,
    allowSameDayBooking: true,
  });

  // Determine role type from path
  const getRoleType = useCallback(() => {
    return "professional";
  }, []);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Get user data from localStorage
      const userStr = localStorage.getItem("ogestyle_user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          setUserData(user);
        } catch (err) {
        }
      }

      const res = await axios.get(`${baseURL}/api/staff/settings`, { headers });
      if (res.data.settings) {
        setSettings((prev) => ({ ...prev, ...res.data.settings }));
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }
      // If settings don't exist yet, just use defaults
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  // Save settings
  const handleSave = async () => {
    // Never save without an actively chosen timezone (browser guard, not backend).
    if (!settings.timezone || !settings.timezone.trim()) {
      setErrorMsg("Please choose your timezone.");
      return;
    }

    try {
      setSaving(true);
      setErrorMsg("");
      setSuccessMsg("");

      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.put(
        `${baseURL}/api/staff/settings`,
        { settings },
        { headers },
      );

      setSuccessMsg("Settings saved successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Unable to save settings.");
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  if (loading) {
    return (
      <StaffSidebarLayout roleType={getRoleType()}>
        <div style={{ padding: "24px", textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚙️</div>
          <p>Loading settings...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout roleType={getRoleType()}>
      <div
        style={{
          padding: isMobile ? "16px" : "24px",
          maxWidth: "900px",
          margin: "0 auto",
          paddingBottom: isMobile ? "100px" : "60px",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{
              fontSize: isMobile ? "20px" : "28px",
              fontWeight: "bold",
              margin: "0 0 8px 0",
            }}
          >
            Personal Settings
          </h1>
          <p
            style={{
              color: "#6b7280",
              fontSize: isMobile ? "13px" : "14px",
              margin: 0,
            }}
          >
            Manage your notification preferences and account settings
          </p>
        </div>

        {errorMsg && (
          <div
            style={{
              padding: "12px 16px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              borderRadius: "8px",
              color: "#991b1b",
              marginBottom: "16px",
            }}
          >
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div
            style={{
              padding: "12px 16px",
              background: "#d1fae5",
              border: "1px solid #10b981",
              borderRadius: "8px",
              color: "#065f46",
              marginBottom: "16px",
            }}
          >
            {successMsg}
          </div>
        )}

        {/* Settings Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Account Information */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              👤 Account Information
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* Email Address - TEMPORARILY HIDDEN */}
              {/* <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginBottom: "4px",
                    }}
                  >
                    Email Address
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "500",
                      color: "#111827",
                    }}
                  >
                    {userData?.email || "Loading..."}
                  </div>
                </div>
                <button
                  onClick={() => setShowEmailChangeModal(true)}
                  style={{
                    padding: "8px 16px",
                    background: "#00509d",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.background = "#003d7a")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.background = "#00509d")
                  }
                >
                  Change Email
                </button>
              </div> */}
              {userData?.firstName && userData?.lastName && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                        marginBottom: "4px",
                      }}
                    >
                      Full Name
                    </div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "500",
                        color: "#111827",
                      }}
                    >
                      {userData.firstName} {userData.lastName}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* MULTI-LOCATION: Temporarily hidden - See SESSION_SUMMARY_DEC_23_2025.md
          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🗺️ Multi-Location Access
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
              Request access to work at multiple business locations and manage your schedules across different branches.
            </p>
            <button
              onClick={() => navigate('/dashboard/professional/multi-location')}
              style={{
                padding: '10px 20px',
                background: '#00509d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#003d7a'}
              onMouseOut={(e) => e.currentTarget.style.background = '#00509d'}
            >
              Manage Multi-Location Access
            </button>
          </div>
          */}

          {/* Email Notifications */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              📧 Email Notifications
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {[
                { key: "newBooking", label: "New booking confirmations" },
                { key: "cancellation", label: "Booking cancellations" },
                { key: "reminder", label: "Upcoming appointment reminders" },
                { key: "review", label: "New customer reviews" },
              ].map((item) => (
                <label
                  key={item.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "6px",
                    background: "#f9fafb",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications[item.key]}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        emailNotifications: {
                          ...prev.emailNotifications,
                          [item.key]: e.target.checked,
                        },
                      }))
                    }
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "15px", color: "#374151" }}>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* SMS Notifications - Premium Feature */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
              opacity: 0.6,
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                color: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: "600",
                letterSpacing: "0.5px",
              }}
            >
              🔒 PREMIUM
            </div>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              📱 SMS Notifications
            </h2>
            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "16px",
              }}
            >
              SMS notifications are available with premium accounts. Email
              notifications are included free!
            </p>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {[
                { key: "newBooking", label: "New booking alerts" },
                { key: "cancellation", label: "Cancellation alerts" },
                {
                  key: "reminder",
                  label: "Appointment reminders (30 min before)",
                },
              ].map((item) => (
                <label
                  key={item.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "not-allowed",
                    padding: "8px",
                    borderRadius: "6px",
                    background: "#f9fafb",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={false}
                    disabled
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "not-allowed",
                    }}
                  />
                  <span style={{ fontSize: "15px", color: "#9ca3af" }}>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Display Preferences */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              🎨 Display Preferences
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Timezone
                </label>
                <select
                  value={settings.timezone}
                  required
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      timezone: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select your timezone...</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Time Format
                </label>
                <select
                  value={settings.timeFormat}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      timeFormat: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  <option value="12h">12-hour (2:30 PM)</option>
                  <option value="24h">24-hour (14:30)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Privacy Settings */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              🔒 Privacy Settings
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {[
                {
                  key: "profileVisible",
                  label: "Make profile visible to customers",
                },
                { key: "showEmail", label: "Show email on public profile" },
                {
                  key: "showPhone",
                  label: "Show phone number on public profile",
                },
              ].map((item) => (
                <label
                  key={item.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "6px",
                    background: "#f9fafb",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={settings[item.key]}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        [item.key]: e.target.checked,
                      }))
                    }
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "15px", color: "#374151" }}>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Booking Preferences */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              📅 Booking Preferences
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "6px",
                  background: "#f9fafb",
                }}
              >
                <input
                  type="checkbox"
                  checked={settings.autoConfirm}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      autoConfirm: e.target.checked,
                    }))
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontSize: "15px", color: "#374151" }}>
                  Auto-confirm bookings (no manual approval required)
                </span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "6px",
                  background: "#f9fafb",
                }}
              >
                <input
                  type="checkbox"
                  checked={settings.allowSameDayBooking}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      allowSameDayBooking: e.target.checked,
                    }))
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontSize: "15px", color: "#374151" }}>
                  Allow same-day bookings
                </span>
              </label>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Advance booking window
                </label>
                <select
                  value={settings.advanceBookingDays}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      advanceBookingDays: Number(e.target.value),
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  <option value={14}>2 weeks</option>
                  <option value={30}>1 month</option>
                  <option value={60}>2 months</option>
                  <option value={90}>3 months</option>
                  <option value={180}>6 months</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div
          style={{
            marginTop: "32px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button
            onClick={() => navigate(`/dashboard/${getRoleType()}`)}
            style={{
              padding: "12px 24px",
              background: "white",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "15px",
              fontWeight: "500",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "12px 24px",
              background: saving ? "#9ca3af" : "#2d6da3",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: saving ? "not-allowed" : "pointer",
              fontSize: "15px",
              fontWeight: "600",
            }}
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>

        {/* Email Change Modal */}
        <EmailChangeModal
          isOpen={showEmailChangeModal}
          onClose={() => setShowEmailChangeModal(false)}
          currentEmail={userData?.email || ""}
          userName={userData?.firstName || "User"}
        />
      </div>
    </StaffSidebarLayout>
  );
};

export default StaffSettings;
